export const CARD_TYPES = [
  "vocabulary",
  "grammar",
  "example",
  "scenario",
  "dialogue",
  "multiple_choice",
  "ordering",
  "fill_blank",
  "text_input",
] as const;

export type CardType = (typeof CARD_TYPES)[number];
export type LessonPhase =
  | "vocabulary"
  | "grammar"
  | "guided_use"
  | "scenario"
  | "dialogue"
  | "practice"
  | "assessment";

export interface FuriganaSegment {
  text: string;
  reading?: string;
}

export interface CardContent {
  japanese?: FuriganaSegment[];
  reading?: string;
  meaningZhTw?: string;
  meaningEn?: string;
  explanationZhTw?: string;
  prompt?: string;
  choices?: string[];
  correctAnswer?: string;
  acceptedAnswers?: string[];
  dialogueTurns?: Array<{
    speaker: string;
    japanese: FuriganaSegment[];
    meaningZhTw?: string;
  }>;
}

export interface LessonCard {
  id: string;
  position: number;
  phase: LessonPhase;
  type: CardType;
  title: string;
  required: boolean;
  content: CardContent;
  source?: {
    contentEntityId?: string;
    exerciseInstanceId?: string;
  };
}

export interface LessonDraft {
  lessonKey: string;
  lessonId: string;
  editVersion: number;
  title: { ja: string; zhTw: string; en: string };
  cards: LessonCard[];
  updatedAt?: string;
  validation?: ValidationResult;
}

export interface ValidationResult {
  valid: boolean;
  errors: Array<{ path: string; message: string }>;
}

export const CARD_LABELS: Record<CardType, string> = {
  vocabulary: "單字",
  grammar: "完整文法句型",
  example: "例句",
  scenario: "情境",
  dialogue: "對話",
  multiple_choice: "選擇題",
  ordering: "語序排列",
  fill_blank: "填空題",
  text_input: "文字輸入",
};

export function phaseForCard(type: CardType): LessonPhase {
  if (type === "vocabulary") return "vocabulary";
  if (type === "grammar") return "grammar";
  if (type === "example") return "guided_use";
  if (type === "scenario") return "scenario";
  if (type === "dialogue") return "dialogue";
  return "practice";
}

export function parseFurigana(input: string): FuriganaSegment[] {
  const result: FuriganaSegment[] = [];
  const pattern = /([^\[\]]+)\[([^\[\]]+)\]/gu;
  let cursor = 0;
  for (const match of input.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) result.push({ text: input.slice(cursor, index) });
    result.push({ text: match[1], reading: match[2] });
    cursor = index + match[0].length;
  }
  if (cursor < input.length) result.push({ text: input.slice(cursor) });
  return result.filter((segment) => segment.text.length > 0);
}

export function serializeFurigana(segments?: FuriganaSegment[]): string {
  return (segments ?? [])
    .map((segment) =>
      segment.reading ? `${segment.text}[${segment.reading}]` : segment.text,
    )
    .join("");
}

function text(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function validateDraft(draft: LessonDraft): ValidationResult {
  const errors: ValidationResult["errors"] = [];
  if (!text(draft.lessonKey)) errors.push({ path: "lessonKey", message: "缺少 Lesson key" });
  if (!text(draft.title.zhTw)) errors.push({ path: "title.zhTw", message: "缺少繁中標題" });
  if (!text(draft.title.ja)) errors.push({ path: "title.ja", message: "缺少日文標題" });
  if (draft.cards.length === 0) errors.push({ path: "cards", message: "至少需要一張卡片" });

  const positions = new Set<number>();
  draft.cards.forEach((card, index) => {
    const path = `cards.${index}`;
    if (!CARD_TYPES.includes(card.type)) errors.push({ path: `${path}.type`, message: "不支援的卡片類型" });
    if (!Number.isInteger(card.position) || card.position < 1) errors.push({ path: `${path}.position`, message: "位置必須是從 1 開始的整數" });
    if (positions.has(card.position)) errors.push({ path: `${path}.position`, message: `位置 ${card.position} 重複` });
    positions.add(card.position);
    if (!text(card.title)) errors.push({ path: `${path}.title`, message: "缺少卡片標題" });

    const japanese = serializeFurigana(card.content.japanese).trim();
    if (["vocabulary", "grammar", "example", "scenario"].includes(card.type) && !japanese) {
      errors.push({ path: `${path}.content.japanese`, message: "缺少日文內容" });
    }
    if (["multiple_choice", "ordering", "fill_blank", "text_input"].includes(card.type) && !text(card.content.prompt)) {
      errors.push({ path: `${path}.content.prompt`, message: "缺少題目" });
    }
    if (card.type === "multiple_choice") {
      const choices = (card.content.choices ?? []).filter(text);
      if (choices.length < 2) errors.push({ path: `${path}.content.choices`, message: "選擇題至少需要兩個選項" });
      if (!text(card.content.correctAnswer) || !choices.includes(card.content.correctAnswer)) {
        errors.push({ path: `${path}.content.correctAnswer`, message: "正解必須存在於選項中" });
      }
    }
    if (card.type === "ordering") {
      const choices = (card.content.choices ?? []).filter(text);
      if (choices.length < 2) errors.push({ path: `${path}.content.choices`, message: "語序排列至少需要兩個詞語" });
      if (!text(card.content.correctAnswer)) {
        errors.push({ path: `${path}.content.correctAnswer`, message: "語序排列缺少正確答案" });
      }
    }
    if (["ordering", "fill_blank", "text_input"].includes(card.type) && !text(card.content.correctAnswer)) {
      errors.push({ path: `${path}.content.correctAnswer`, message: "缺少正確答案" });
    }
    if (card.type === "dialogue" && (card.content.dialogueTurns?.length ?? 0) < 2) {
      errors.push({ path: `${path}.content.dialogueTurns`, message: "對話至少需要兩句" });
    }
  });

  const expected = draft.cards.map((_, index) => index + 1);
  const actual = [...positions].sort((a, b) => a - b);
  if (actual.some((value, index) => value !== expected[index])) {
    errors.push({ path: "cards.position", message: "卡片位置必須連續，不能跳號" });
  }
  return { valid: errors.length === 0, errors };
}
